/**
 * @param CountryCodeInputBase Base widget for extending
 * @returns BackInStockPhoneInput widget
 */
function BackInStockPhoneInputClassCreator(CountryCodeInputBase: import('./CountryCodeInput').TCountryCodeInput) {
    /**
     * @category widgets
     * @subcategory forms
     * @class BackInStockPhoneInput
     * @augments CountryCodeInput
     * @classdesc Phone input for the BackInStock SMS subscription form.
     * Always initialises the intl-tel-input plugin with US (+1) fixed and
     * dropdown disabled — regardless of the global `isCountryCodeInputEnabled`
     * site preference. `getValue()` returns the full E.164 number (e.g. +17377812321).
     */
    class BackInStockPhoneInput extends CountryCodeInputBase {
        containsAllowedSymbols(): boolean {
            // intl-tel-input formats the raw field value as "(737) 781-2321" which fails the
            // digits-only parse pattern. Test the E.164 number instead.
            const e164 = this.intlTelInstance?.getNumber() || '';

            if (!e164) {
                return true;
            }

            const regexStr = this.prefs().validationConfig.patterns.parse;

            return new RegExp(regexStr).test(e164);
        }

        initPlugin() {
            const phoneInputField = this.ref('field').get<HTMLInputElement>();

            if (!phoneInputField) {
                return;
            }

            this.intlTelInstance = this.intlTelProto!(phoneInputField, {
                initialCountry: 'us',
                onlyCountries: ['US'],
                nationalMode: true,
                allowDropdown: false,
                showSelectedDialCode: true
            } as any);

            // Block non-digit input (letters, symbols). Digits and control keys pass through;
            // intl-tel-input handles national formatting of the allowed digits.
            phoneInputField.addEventListener('beforeinput', (e: InputEvent) => {
                if (e.data !== null && /\D/.test(e.data)) {
                    e.preventDefault();
                }
            });
        }
    }

    return BackInStockPhoneInput;
}

export type TBackInStockPhoneInput = ReturnType<typeof BackInStockPhoneInputClassCreator>;

export type TBackInStockPhoneInputInstance = InstanceType<TBackInStockPhoneInput>;

export default BackInStockPhoneInputClassCreator;
